import { clientStorage } from "@/lib/clientStorage";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";

// Aid Planner matters: shared through the portal, cached on this computer.
//
// A matter used to live only in this browser (on the portal, only in this tab), so the
// extension reminders it drives were seen by one person, until the tab closed. Matters now
// save to /api/aid-planner-matters, where everyone at the firm sees the same ones.
//
// The browser copy stays, for two reasons: the page works from it synchronously, as it
// always has, and it is the only place a letter's PDF lives. The portal keeps each letter's
// name and what was read from it, not the file (DATA_OWNERSHIP.md), so a colleague sees the
// letter was received but opens the PDF from the firm's own files.
//
// Every save goes to the browser at once and to the portal a moment later. Each cached
// matter carries _portal: { version, dirty } - the portal version it was last in step with,
// and whether it has changes the portal has not seen. A save that finds someone else has
// saved first gets their copy instead, and the page is told.
//
// LACW local test mode has no server session, so there it stays on this computer only.
//
// Each cached matter also carries _firm: the firm it is filed under. Staff only ever see
// their own firm, but an administrator works in every firm's Aid Planner from one browser,
// so the page says which firm it is for (listLocalMatters, syncMattersWithPortal and
// saveMatterToDevice all take it) and a new matter is filed under that firm, not "admin".
// With no firm given they behave as before: every matter, filed by the session.

const STORAGE_KEY = "aid-planner-matters-v1";
const LAST_OPENED_KEY = "aid-planner-last-opened-v1";
const API = "/api/aid-planner-matters";
const PUSH_DELAY_MS = 1500;

export const AID_PLANNER_SYNC_EVENT = "aid-planner-sync";

function readStore() {
  if (typeof window === "undefined") return {};
  try {
    const raw = clientStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  if (typeof window === "undefined") return;
  try {
    clientStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    // Propagate quota errors so callers can show a useful message
    if (err && (err.name === "QuotaExceededError" || err.code === 22)) {
      throw new Error("Storage quota exceeded — the file is too large to save in the browser. Please clear some space or use 'Save / Print to PDF' instead.");
    }
    throw err;
  }
}

// --- sync status, for the page to show ---------------------------------------------------

let syncStatus = { state: "idle", message: "" };

function setSyncStatus(state, message = "", detail = {}) {
  syncStatus = { state, message, at: new Date().toISOString() };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AID_PLANNER_SYNC_EVENT, { detail: { ...syncStatus, ...detail } }));
  }
}

export function getAidPlannerSyncStatus() {
  return syncStatus;
}

export function portalSyncAvailable() {
  return typeof window !== "undefined" && typeof fetch === "function" && !isLacwLocalTestMode();
}

// --- the browser copy --------------------------------------------------------------------

const pushTimers = new Map();

// Whether a cached matter belongs in this firm's list. A matter not yet tagged and never
// shared was made on this computer, so it is shown, and shared under this firm.
export function matterInFirm(matter, firm = "") {
  if (!matter) return false;
  if (!firm) return true;
  if (matter._firm) return matter._firm === firm;
  return !matter._portal?.version;
}

export function saveMatterToDevice(matter, { fromPortal = false, firm = "" } = {}) {
  if (!matter?.matterId) return;
  const store = readStore();
  const previous = store[matter.matterId];
  // The page saves whenever a matter is opened; a save that changes nothing is not an edit,
  // and must not reach the portal as one.
  const unchanged = !fromPortal && previous
    && JSON.stringify(withoutLocalOnly(previous)) === JSON.stringify(withoutLocalOnly(matter));
  const matterFirm = matter._firm || previous?._firm || firm;
  store[matter.matterId] = {
    ...matter,
    ...(matterFirm ? { _firm: matterFirm } : {}),
    _portal: fromPortal
      ? matter._portal
      : unchanged
        ? previous._portal
        : { version: previous?._portal?.version || matter._portal?.version || "", dirty: true },
  };
  writeStore(store);
  if (!fromPortal && !unchanged) schedulePush(matter.matterId);
}

export function loadMatterFromDevice(matterId) {
  if (!matterId) return null;
  const store = readStore();
  return store[matterId] || null;
}

export function listLocalMatters(firm = "") {
  const store = readStore();
  return Object.values(store).filter((matter) => matterInFirm(matter, firm)).sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
}

export function deleteLocalMatter(matterId) {
  const store = readStore();
  const wasShared = Boolean(store[matterId]?._portal?.version);
  delete store[matterId];
  writeStore(store);
  if (getLastOpenedMatterId() === matterId) {
    clientStorage.removeItem(LAST_OPENED_KEY);
  }
  clearTimeout(pushTimers.get(matterId));
  pushTimers.delete(matterId);
  if (wasShared && portalSyncAvailable()) {
    fetch(`${API}?matter_key=${encodeURIComponent(matterId)}`, { method: "DELETE", credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) setSyncStatus("error", "The matter was removed here but could not be removed from the portal.");
      })
      .catch(() => setSyncStatus("offline", "The matter was removed here but the portal could not be reached."));
  }
}

export function getLastOpenedMatterId() {
  if (typeof window === "undefined") return "";
  return clientStorage.getItem(LAST_OPENED_KEY) || "";
}

export function setLastOpenedMatterId(matterId) {
  if (typeof window === "undefined" || !matterId) return;
  clientStorage.setItem(LAST_OPENED_KEY, matterId);
}

// --- to and from the portal --------------------------------------------------------------

// A portal copy of a matter, with any letter files this computer holds put back.
export function mergePortalMatter(portalPlanner, local, version, firm = "") {
  const localFiles = new Map(
    (local?.uploadedLetters || []).filter((letter) => letter?.fileData).map((letter) => [letter.id, letter.fileData]),
  );
  const matterFirm = firm || local?._firm || "";
  return {
    ...portalPlanner,
    uploadedLetters: (portalPlanner.uploadedLetters || []).map((letter) =>
      localFiles.has(letter.id) ? { ...letter, fileData: localFiles.get(letter.id) } : letter),
    ...(matterFirm ? { _firm: matterFirm } : {}),
    _portal: { version, dirty: false },
  };
}

function withoutLocalOnly(matter) {
  const { _portal, _firm, ...rest } = matter;
  return {
    ...rest,
    uploadedLetters: (rest.uploadedLetters || []).map(({ fileData, ...letter }) => (fileData ? { ...letter, fileHeldLocally: true } : letter)),
  };
}

function schedulePush(matterId) {
  if (!portalSyncAvailable()) {
    setSyncStatus("local", "Saved on this computer only (test mode).");
    return;
  }
  clearTimeout(pushTimers.get(matterId));
  pushTimers.set(matterId, setTimeout(() => {
    pushTimers.delete(matterId);
    pushMatter(matterId);
  }, PUSH_DELAY_MS));
}

async function pushMatter(matterId) {
  const matter = loadMatterFromDevice(matterId);
  if (!matter || !matter._portal?.dirty) return;
  setSyncStatus("saving", "Saving to the portal…");
  try {
    const response = await fetch(API, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      // brand only counts for an administrator; staff are always filed under their own firm.
      body: JSON.stringify({
        planner: withoutLocalOnly(matter),
        expected_version: matter._portal.version || "",
        ...(matter._firm ? { brand: matter._firm } : {}),
      }),
    });
    const body = await response.json().catch(() => ({}));

    if (response.status === 409 && body.current?.planner) {
      // Someone else saved first. Theirs wins; this computer's letter files are kept.
      const theirs = mergePortalMatter(body.current.planner, matter, body.current.updated_at, body.current.brand);
      saveMatterToDevice(theirs, { fromPortal: true });
      setSyncStatus("conflict", `${body.current.updated_by_name || "A colleague"} changed this matter at the same time. Their version has been loaded; please re-check your last change.`, { matterId });
      notifyMatterChanged(matterId);
      return;
    }
    if (response.status === 401 || response.status === 403) {
      setSyncStatus("offline", "Saved on this computer only: sign in to the portal to share matters with your firm.");
      return;
    }
    if (!response.ok) {
      setSyncStatus("error", body.error || "The matter could not be saved to the portal. It is saved on this computer.");
      return;
    }

    // Saved. Only clear "dirty" if nothing changed here while the save was in flight.
    const latest = loadMatterFromDevice(matterId);
    if (latest) {
      const changedMeanwhile = JSON.stringify(withoutLocalOnly(latest)) !== JSON.stringify(withoutLocalOnly(matter));
      const savedFirm = latest._firm || body.brand || "";
      saveMatterToDevice({ ...latest, ...(savedFirm ? { _firm: savedFirm } : {}), _portal: { version: body.updated_at, dirty: changedMeanwhile } }, { fromPortal: true });
      if (changedMeanwhile) schedulePush(matterId);
    }
    setSyncStatus("synced", "Saved to the portal.");
  } catch {
    setSyncStatus("offline", "The portal could not be reached. Changes are saved on this computer and will be sent when it can be.");
  }
}

function notifyMatterChanged(matterId) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("matter-updated", { detail: { matterId } }));
  }
}

// Bring this computer into step with the firm's matters. Returns what changed.
// firm: the firm whose matters to bring in step. Matters this computer holds for any other
// firm are left exactly as they are.
export async function syncMattersWithPortal(firm = "") {
  if (!portalSyncAvailable()) {
    setSyncStatus("local", "Test mode: matters are saved on this computer only.");
    return { changed: false };
  }
  setSyncStatus("syncing", "Loading your firm's matters…");
  let rows;
  try {
    // ?brand= only narrows an administrator's list; staff only ever get their own firm.
    const response = await fetch(firm ? `${API}?brand=${encodeURIComponent(firm)}` : API, { credentials: "same-origin" });
    if (response.status === 401 || response.status === 403) {
      setSyncStatus("offline", "Saved on this computer only: sign in to the portal to share matters with your firm.");
      return { changed: false };
    }
    if (!response.ok) throw new Error();
    rows = await response.json();
  } catch {
    setSyncStatus("offline", "The portal could not be reached. Showing the matters saved on this computer.");
    return { changed: false };
  }

  const store = readStore();
  const next = {};
  const seen = new Set();
  let changed = false;
  const toUpload = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const key = row.matter_key;
    if (!key || !row.planner) continue;
    seen.add(key);
    const local = store[key];
    const rowFirm = row.brand || local?._firm || firm;
    const tagged = (matter) => (rowFirm ? { ...matter, _firm: rowFirm } : matter);
    if (local?._portal?.dirty && local._portal.version === row.updated_at) {
      // Changed here since the portal copy this computer last saw; send it.
      next[key] = tagged(local);
      toUpload.push(key);
    } else if (local?._portal?.version === row.updated_at && !local?._portal?.dirty) {
      next[key] = tagged(local);
      if (rowFirm !== (local._firm || "")) changed = true;
    } else {
      next[key] = mergePortalMatter(row.planner, local, row.updated_at, rowFirm);
      changed = true;
      if (local?._portal?.dirty) toUpload.push(key); // will come back as a conflict, and say so
    }
  }

  for (const [key, local] of Object.entries(store)) {
    if (seen.has(key)) continue;
    if (firm && !matterInFirm(local, firm)) {
      // Another firm's matter, or one shared before matters were tagged with their firm:
      // not this firm's list to judge, so it is kept as it is.
      next[key] = local;
      continue;
    }
    if (local?._portal?.version) {
      // It was shared, and the portal no longer has it: deleted by a colleague, or expired.
      changed = true;
      continue;
    }
    // Only ever on this computer: share it with the firm.
    next[key] = { ...local, ...(firm ? { _firm: firm } : {}), _portal: { version: "", dirty: true } };
    toUpload.push(key);
  }

  writeStore(next);
  for (const key of toUpload) schedulePush(key);
  setSyncStatus("synced", toUpload.length
    ? `Sharing ${toUpload.length} matter${toUpload.length === 1 ? "" : "s"} from this computer with your firm…`
    : "Up to date with your firm's matters.");
  return { changed: changed || toUpload.length > 0 };
}
